import React, { useState, useEffect, useCallback } from 'react';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { Users, Building2, AlertTriangle, BedDouble, Search, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import ClientProfile from '@/components/ClientProfile';

interface SearchResultItem {
  id: string;
  type: 'client' | 'room' | 'alert' | 'site' | 'staff';
  title: string;
  subtitle?: string;
  status?: string;
  data?: any;
}

export function GlobalSearchCommand() {
  const { } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timeout = setTimeout(() => {
      performSearch(query.trim());
    }, 250);

    return () => clearTimeout(timeout);
  }, [query]);

  const performSearch = useCallback(async (term: string) => {
    if (!term) return;
    setIsSearching(true);

    const sanitized = term.replace(/[,.*%]/g, '').slice(0, 50);
    if (!sanitized) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    const wildcardTerm = `%${sanitized}%`;
    const allResults: SearchResultItem[] = [];

    try {
      // Search clients via clients_safe view
      const { data: clients } = await supabase
        .from('clients_safe')
        .select('id, first_name, last_name, status, room_id, site_id, date_of_birth, photo_url, cares_id')
        .or(`first_name.ilike.${wildcardTerm},last_name.ilike.${wildcardTerm},cares_id.ilike.${wildcardTerm}`)
        .is('archived_at', null)
        .limit(8);

      if (clients?.length) {
        // Fetch room numbers for matched clients
        const roomIds = clients.filter(c => c.room_id).map(c => c.room_id!);
        const siteIds = clients.filter(c => c.site_id).map(c => c.site_id!);

        const [roomsRes, sitesRes] = await Promise.all([
          roomIds.length > 0
            ? supabase.from('rooms').select('id, room_number').in('id', roomIds)
            : Promise.resolve({ data: [] }),
          siteIds.length > 0
            ? supabase.from('sites_public').select('id, name').in('id', siteIds)
            : Promise.resolve({ data: [] }),
        ]);

        const roomMap = new Map((roomsRes.data || []).map(r => [r.id, r.room_number]));
        const siteMap = new Map((sitesRes.data || []).map(s => [s.id, s.name]));

        clients.forEach(client => {
          const roomNum = client.room_id ? roomMap.get(client.room_id) : null;
          const siteName = client.site_id ? siteMap.get(client.site_id) : null;
          allResults.push({
            id: client.id,
            type: 'client',
            title: `${client.first_name} ${client.last_name}`,
            subtitle: [roomNum ? `Room ${roomNum}` : null, siteName].filter(Boolean).join(' • ') || 'No room assigned',
            status: client.status || 'unbedded',
            data: {
              id: client.id,
              name: `${client.first_name} ${client.last_name}`,
              room: roomNum || '',
              status: client.status || 'unbedded',
              photo: client.photo_url,
              age: client.date_of_birth
                ? Math.floor((Date.now() - new Date(client.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
                : 30,
              caseWorker: 'Unassigned',
            },
          });
        });
      }

      // Search rooms
      const { data: rooms } = await supabase
        .from('rooms')
        .select('id, room_number, capacity, is_occupied, current_occupancy, site_id')
        .ilike('room_number', wildcardTerm)
        .limit(5);

      if (rooms?.length) {
        const siteIds = rooms.filter(r => r.site_id).map(r => r.site_id);
        const { data: roomSites } = siteIds.length > 0
          ? await supabase.from('sites_public').select('id, name').in('id', siteIds)
          : { data: [] };
        const siteMap = new Map((roomSites || []).map(s => [s.id, s.name]));

        rooms.forEach(room => {
          allResults.push({
            id: room.id,
            type: 'room',
            title: `Room ${room.room_number}`,
            subtitle: `${room.current_occupancy || 0}/${room.capacity || 1} beds • ${siteMap.get(room.site_id) || 'Unknown site'}`,
            status: room.is_occupied ? 'occupied' : 'available',
          });
        });
      }

      // Search alerts
      const { data: alerts } = await supabase
        .from('alerts')
        .select('id, title, type, status, description, created_at')
        .or(`title.ilike.${wildcardTerm},description.ilike.${wildcardTerm}`)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(5);

      if (alerts?.length) {
        alerts.forEach(alert => {
          allResults.push({
            id: alert.id,
            type: 'alert',
            title: alert.title,
            subtitle: alert.description?.slice(0, 80),
            status: alert.type,
          });
        });
      }

      // Search sites
      const { data: sites } = await supabase
        .from('sites_public')
        .select('id, name, address, capacity')
        .ilike('name', wildcardTerm)
        .limit(5);

      if (sites?.length) {
        sites.forEach(site => {
          allResults.push({
            id: site.id,
            type: 'site',
            title: site.name,
            subtitle: site.address || `Capacity: ${site.capacity || 'N/A'}`,
          });
        });
      }

      // Search staff (profiles)
      const { data: staff } = await supabase
        .from('profiles')
        .select('id, full_name')
        .ilike('full_name', wildcardTerm)
        .limit(5);

      if (staff?.length) {
        const staffIds = staff.map(s => s.id);
        const { data: roles } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', staffIds);
        const roleMap = new Map((roles || []).map(r => [r.user_id, r.role]));
        staff.forEach(s => {
          allResults.push({
            id: s.id,
            type: 'staff',
            title: s.full_name || 'Unknown Staff',
            subtitle: roleMap.get(s.id) ? String(roleMap.get(s.id)).replace(/_/g, ' ') : 'Staff',
          });
        });
      }

      setResults(allResults);
    } catch (err) {
      console.error('Global search error:', err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSelect = (result: SearchResultItem) => {
    if (result.type === 'client' && result.data) {
      setSelectedClient(result.data);
    }
    setOpen(false);
    setQuery('');
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'client': return <Users className="h-4 w-4 text-primary" />;
      case 'room': return <BedDouble className="h-4 w-4 text-muted-foreground" />;
      case 'alert': return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'site': return <Building2 className="h-4 w-4 text-primary" />;
      case 'staff': return <UserCog className="h-4 w-4 text-primary" />;
      default: return <Search className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (type: string, status?: string) => {
    if (!status) return null;
    if (type === 'client') {
      return (
        <Badge variant={status === 'checked-in' ? 'default' : 'secondary'} className="text-[10px] h-5">
          {status}
        </Badge>
      );
    }
    if (type === 'alert') {
      return (
        <Badge variant={status === 'critical' ? 'destructive' : 'outline'} className="text-[10px] h-5">
          {status}
        </Badge>
      );
    }
    if (type === 'room') {
      return (
        <Badge variant={status === 'occupied' ? 'secondary' : 'outline'} className="text-[10px] h-5">
          {status}
        </Badge>
      );
    }
    return null;
  };

  const groupedResults = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, SearchResultItem[]>);

  const groupLabels: Record<string, string> = {
    client: 'Clients',
    room: 'Rooms',
    alert: 'Active Alerts',
    site: 'Facilities',
    staff: 'Staff',
  };

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-56 md:w-64 justify-start pl-3 pr-12"
        onClick={() => setOpen(true)}
      >
        <Search className="h-4 w-4 mr-2 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search clients (name or Cares ID), rooms, alerts..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList className="max-h-[400px]">
          {!query && (
            <CommandEmpty>
              Start typing to search across clients by name or Cares ID, rooms, alerts, and facilities.
            </CommandEmpty>
          )}
          {query && results.length === 0 && !isSearching && (
            <CommandEmpty>No results found for "{query}"</CommandEmpty>
          )}
          {query && isSearching && results.length === 0 && (
            <CommandEmpty>Searching...</CommandEmpty>
          )}

          {Object.entries(groupedResults).map(([type, items], idx) => (
            <React.Fragment key={type}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={groupLabels[type] || type}>
                {items.map((result) => (
                  <CommandItem
                    key={result.id}
                    value={`${result.title} ${result.subtitle || ''}`}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    {getIcon(result.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{result.title}</span>
                        {getStatusBadge(result.type, result.status)}
                      </div>
                      {result.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </React.Fragment>
          ))}
        </CommandList>
      </CommandDialog>

      {/* Client Profile Dialog */}
      {selectedClient && (
        <ClientProfile
          client={{
            id: selectedClient.id,
            name: selectedClient.name,
            age: selectedClient.age || 30,
            room: selectedClient.room,
            status: selectedClient.status,
            caseWorker: selectedClient.caseWorker || 'Unassigned',
            photo: selectedClient.photo,
            checkInTime: new Date().toISOString(),
          }}
          isOpen={!!selectedClient}
          onClose={() => setSelectedClient(null)}
          getClientPhoto={() => selectedClient.photo || ''}
        />
      )}
    </>
  );
}
